import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import { BillingLineSide } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useContext } from 'react';

import CreatedColumn from '~components/Load/LoadsTableColumns/CreatedColumn';
import DatetimeColumn from '~components/Load/LoadsTableColumns/DatetimeColumn';
import { Job } from '~hooks/useJob/models';
import { Load } from '~hooks/useLoad/models';
import { Ticket } from '~hooks/useTickets/models';
import MaterialColumn from '~pages/Approvals/Files/components/LoadsTable/Columns/MaterialColumn';
import PickupAndDropoffColumn from '~pages/Approvals/Files/components/LoadsTable/Columns/PickupAndDropoffColumn';
import QuantityColumn from '~pages/Approvals/Files/components/LoadsTable/Columns/QuantityColumn';
import TicketColumn from '~pages/Approvals/Files/components/LoadsTable/Columns/TicketColumn';
import JobsQueryContext from '~pages/Approvals/Files/components/OrdersList/ExpandedBlock/JobsQueryContext';
import { useStores } from '~store';
import { flaggedLoadRowSx } from '~utils/loads/load-utils';
import { isViewerAccountCustomer } from '~utils/user/user-utils';

import {
  CHARGES_CELL_SX,
  CHARGES_GRID_TEMPLATE_COLUMNS,
  chargesGridColumn,
  chargesRowTextSx,
  NO_VALUE_PLACEHOLDER,
} from '../chargesGrid';
import {
  getLinesSettledByUnit,
  getTotalWithTax,
  LoadCharges,
} from '../groupBillingLines';
import ChargeAmountCell from './ChargeAmountCell';
import ChargeLinesCheckbox from './ChargeLinesCheckbox';
import ChargesLoadActions from './ChargesLoadActions';
import SettlementNumberChip from './SettlementNumberChip';

function getSideAmount(side?: LoadCharges['payable']) {
  if (!side) {
    return null;
  }

  if (side.isRejected) {
    return side.rejectedTotal;
  }

  return getTotalWithTax(side);
}

interface ChargesLoadRowProps {
  charges?: LoadCharges;
  job: Job;
  load: Load;
  onClick: (loadId: string, ticket?: Ticket) => void;
  ticket?: Ticket;
}

export default function ChargesLoadRow({
  charges,
  job,
  load,
  onClick,
  ticket,
}: ChargesLoadRowProps) {
  const { userStore } = useStores();
  const { order } = useContext(JobsQueryContext);

  const isReadOnlyTicket = isViewerAccountCustomer(userStore, job.order?.account);
  const canCreateTicket = !isReadOnlyTicket && userStore.getPermissions().canCreateTicket;
  const isRowClickable = ticket ? true : canCreateTicket;
  const linesSettledByUnit = charges ? getLinesSettledByUnit(charges.lines) : [];

  const handleRowClick = useCallback(() => {
    onClick(load.id, ticket);
  }, [load.id, onClick, ticket]);

  return (
    <Box
      data-test-id="charges-load-row"
      onClick={isRowClickable ? handleRowClick : undefined}
      sx={(theme) => ({
        alignItems: 'start',
        borderBottom: `solid 1px ${theme.semantic.divider}`,
        display: 'grid',
        gridTemplateColumns: CHARGES_GRID_TEMPLATE_COLUMNS,
        ...chargesRowTextSx(theme),
        ...(isRowClickable ? { cursor: 'pointer' } : {}),
        ...(ticket?.potentialDuplicate && {
          ...flaggedLoadRowSx(theme),
          bgcolor: alpha(theme.semantic.rowFlaggedBg, 0.5),
        }),
      })}
    >
      <Box sx={{ ...CHARGES_CELL_SX, gridColumn: chargesGridColumn.select }}>
        <ChargeLinesCheckbox
          label={t('approvals.approve_charges.selection.load')}
          lines={linesSettledByUnit}
        />
      </Box>
      <DatetimeColumn
        sx={{ ...CHARGES_CELL_SX, gridColumn: chargesGridColumn.time }}
        ticket={ticket}
      />
      <CreatedColumn
        boldTime
        createdAt={ticket?.createdAt}
        sx={{ ...CHARGES_CELL_SX, gridColumn: chargesGridColumn.created }}
      />
      <TicketColumn
        job={job}
        sx={{ ...CHARGES_CELL_SX, gridColumn: chargesGridColumn.ticket }}
        ticket={ticket}
      />
      <PickupAndDropoffColumn
        load={load}
        sx={{ ...CHARGES_CELL_SX, gridColumn: chargesGridColumn.route }}
      />
      <MaterialColumn
        emptyPlaceholder={NO_VALUE_PLACEHOLDER}
        job={job}
        load={load}
        sx={{ ...CHARGES_CELL_SX, gridColumn: chargesGridColumn.material }}
        ticket={ticket}
      />
      <QuantityColumn
        emptyPlaceholder={NO_VALUE_PLACEHOLDER}
        job={job}
        load={load}
        sx={{
          ...CHARGES_CELL_SX,
          gridColumn: chargesGridColumn.ticketQuantity,
          '& .MuiTypography-root': { fontWeight: 700 },
        }}
        ticket={ticket}
      />
      <ChargeAmountCell
        amount={getSideAmount(charges?.payable)}
        isMaterialRateItemized={order.materialRateItemized}
        isRejected={charges?.payable?.isRejected}
        materialRate={charges?.payable?.materialRate}
        materialRateType={charges?.payable?.materialRateType}
        rate={charges?.payable?.rate}
        rateType={charges?.payable?.rateType}
        rejectedReason={charges?.payable?.rejectedReason}
        taxAmount={charges?.payable?.taxTotal}
        wasRate={charges?.payable?.wasRate}
        sx={{ gridColumn: chargesGridColumn.payableAmount }}
      />
      <ChargeAmountCell
        amount={getSideAmount(charges?.receivable)}
        isMaterialRateItemized={order.materialRateItemized}
        isRejected={charges?.receivable?.isRejected}
        materialRate={charges?.receivable?.materialRate}
        materialRateType={charges?.receivable?.materialRateType}
        rate={charges?.receivable?.rate}
        rateType={charges?.receivable?.rateType}
        rejectedReason={charges?.receivable?.rejectedReason}
        taxAmount={charges?.receivable?.taxTotal}
        wasRate={charges?.receivable?.wasRate}
        sx={{ gridColumn: chargesGridColumn.receivableAmount }}
      />
      <SettlementNumberChip
        hasUnrejectedLines={charges?.payable?.isRejected === false}
        hasUnsettledLines={charges?.payable?.hasUnsettledLines}
        isApproved={charges?.payable?.isSettlementApproved}
        settlementNumbers={charges?.payable?.settlementNumbers ?? []}
        side={BillingLineSide.PAYABLE}
        sx={{ gridColumn: chargesGridColumn.payableNumber }}
      />
      <SettlementNumberChip
        hasUnrejectedLines={charges?.receivable?.isRejected === false}
        hasUnsettledLines={charges?.receivable?.hasUnsettledLines}
        isApproved={charges?.receivable?.isSettlementApproved}
        settlementNumbers={charges?.receivable?.settlementNumbers ?? []}
        side={BillingLineSide.RECEIVABLE}
        sx={{ gridColumn: chargesGridColumn.invoiceNumber }}
      />
      <Box sx={{ ...CHARGES_CELL_SX, gridColumn: chargesGridColumn.status }}>
        <ChargesLoadActions charges={charges} job={job} load={load} ticket={ticket} />
      </Box>
    </Box>
  );
}
